/**
 * SF-5055 — "Help Desk" tab on the company page: every help desk setting in one place.
 *
 * The Help Desk on/off switch itself (`help_desk_module_enabled`, the web app's module switch) is
 * on the Feature Flags tab, and the page shows this tab only while it is on (or could not be read).
 *
 * Each control saves ONE field on its own through the page's saver (one save at a time per
 * company, see companySaveStore), and every value is the one the web app uses:
 *  - Help desk plan (`helpDeskPlan`): Premium, Lite or Mix, the web app's company edit field.
 *    Missing = Lite. Lite gets no Eva helpdesk replies: moving to Lite switches them off and puts
 *    the mode back to drafts (sf-api does both in the same save), so it asks first while they are
 *    on. The tab then shows what sf-api returned.
 *  - Ticket emails (`help_desk_email_mode`, SF-5126): off, or forwarded from the company's own
 *    address. Missing = off. sf-api makes the forwarding address the first time forwarding goes
 *    on (the PATCH reply carries it), and refuses while Help Desk is off.
 *  - Forwarding address (`help_desk_forwarding_address`, SF-5126): shown with copy while
 *    forwarding is on; only emails that arrive there become tickets. "Make a new address" goes to
 *    its own sf-api route through the same saver (`send`), so it follows the same save rules.
 *  - Support inbox (`assets_support_email`): the company's own support address, shown for
 *    reference only since SF-5126. Trimmed; empty = none; sf-api refuses an address another
 *    company has.
 *  - Ratings create tickets (`allowRatingHelpdesk`): the company half; each location has its own
 *    switch in the web app, and both must be on.
 *  - Eva helpdesk replies (EvaHelpdeskRepliesFlag, moved from Feature Flags): on/off, mode,
 *    sign-off. Only on Premium or Mix (Mix: Premium locations only).
 *  - Returning-customer window (`eva_helpdesk_repeat_window_days`): 0 to 365 days, default 30.
 *    0 switches the check off: an earlier ticket from the same customer never stops Eva replying.
 *
 * Everyone who can see the company sees the tab; changing anything needs company edit rights
 * (admin:companies:write) and the company on the staff member's list. sf-api checks both.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Input, InputNumber, Popconfirm, Radio, Space, Typography } from 'antd';
import { FlagSwitch } from './FlagSwitch';
import { EvaHelpdeskRepliesFlag } from './EvaHelpdeskRepliesFlag';
import { evaRepliesPlanAllowed } from './helpDeskPlan';
import type { PendingSave, SaveFieldOptions } from './useSaveCompanyField';
import { useNewForwardingAddress } from '../api/helpDeskEmailClient';
import type { Company, HelpDeskEmailMode, HelpDeskPlan } from '../list';

const { Text } = Typography;

export const REPEAT_WINDOW_DEFAULT = 30;
export const REPEAT_WINDOW_MIN = 0;
export const REPEAT_WINDOW_MAX = 365;

const PLAN_OPTIONS: Array<{ value: HelpDeskPlan; label: string; note: string }> = [
  {
    value: 'premium',
    label: 'Premium',
    note: 'Staff can email, text and use every help desk tool.',
  },
  {
    value: 'lite',
    label: 'Lite',
    note: 'Staff cannot email or text customers. No Eva helpdesk replies.',
  },
  {
    value: 'mix',
    label: 'Mix',
    note: 'Each location has its own plan (set on the location in the web app). Eva helpdesk replies run only for Premium locations.',
  },
];

const EMAIL_MODE_OPTIONS: Array<{ value: HelpDeskEmailMode; label: string; note: string }> = [
  {
    value: 'off',
    label: 'Off',
    note: 'Emails do not create help desk tickets for this company.',
  },
  {
    value: 'forwarding',
    label: "Forwarded from the company's own address",
    note: 'The company forwards its own support mailbox to the address below. Only emails that arrive at that address become tickets.',
  },
];

const NEW_ADDRESS_MADE = 'New forwarding address made';

const LITE_SWITCHES_EVA_OFF_TEXT = 'Eva helpdesk replies will be switched off.';
const EVA_STARTS_TEXT =
  'Eva helpdesk replies are saved as on for this company, so Eva starts replying to its tickets.';

const NOT_ON_LIST_NOTE = 'This company is not on your list of companies, so you can only view it.';
export const SCOPE_CHECK_FAILED_NOTE = 'Could not check your company edit access. Try again.';
const COMPANY_WRITE_NOTE = 'Changing this needs company edit rights (admin:companies:write).';

const daysText = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
/** The returning-customer note for the saved window (0 = the check is off). */
const repeatWindowNote = (savedDays: number) =>
  `${
    savedDays === 0
      ? 'Off: Eva can reply herself to a customer who has sent other tickets.'
      : `A ticket from someone who sent another one in the last ${daysText(savedDays)} counts as a returning customer, and Eva never replies to it herself.`
  } 0 turns this off. ${REPEAT_WINDOW_MIN} to ${REPEAT_WINDOW_MAX} days; ${REPEAT_WINDOW_DEFAULT} by default.`;

export interface HelpDeskTabProps {
  company: Company;
  /** Refine companies:edit (admin:companies:write). */
  canEditCompany: boolean;
  /** SF-4668: false when this company is not on the staff member's list. Defaults to true. */
  companyOnList?: boolean;
  /**
   * The company list lookup failed and no answer ever arrived: `companyOnList` false then means
   * "unknown", not "not on your list". Everything stays locked, with a retry.
   */
  scopeCheckFailed?: boolean;
  onRetryScope?: () => void;
  permissionsLoading: boolean;
  saver: {
    save: <K extends keyof Company>(
      field: K,
      value: Company[K],
      successText: string,
      options?: SaveFieldOptions,
    ) => Promise<boolean>;
    pending: PendingSave | null;
    busy: boolean;
  };
}

const Row: React.FC<{
  title: string;
  note: React.ReactNode;
  testId: string;
  children: React.ReactNode;
}> = ({ title, note, testId, children }) => (
  <div data-testid={testId}>
    <Text strong>{title}</Text>
    <div style={{ marginTop: 4 }}>{children}</div>
    <Text type="secondary">{note}</Text>
  </div>
);

export const HelpDeskTab: React.FC<HelpDeskTabProps> = ({
  company,
  canEditCompany,
  companyOnList = true,
  scopeCheckFailed = false,
  onRetryScope,
  permissionsLoading,
  saver,
}) => {
  const pendingOf = (field: keyof Company): unknown =>
    saver.pending?.field === field ? saver.pending.value : undefined;
  const pendingBool = (field: keyof Company): boolean | null => {
    const v = pendingOf(field);
    return typeof v === 'boolean' ? v : null;
  };

  // While permissions load the controls are just locked, with no refusal note.
  const reason = permissionsLoading
    ? null
    : scopeCheckFailed
      ? SCOPE_CHECK_FAILED_NOTE
      : !companyOnList
        ? NOT_ON_LIST_NOTE
        : !canEditCompany
          ? COMPANY_WRITE_NOTE
          : null;
  const locked = permissionsLoading || saver.busy || reason !== null;

  // ---- Plan ---------------------------------------------------------------
  const savedPlan: HelpDeskPlan = company.helpDeskPlan ?? 'lite';
  const pendingPlan = pendingOf('helpDeskPlan') as HelpDeskPlan | undefined;
  const evaRepliesSavedOn = company.eva_helpdesk_replies_enabled === true;
  // Which question a plan change asks first, if any. Moving to Lite while replies are on switches
  // them off. A Lite company saved with them on (older data) starts getting them on Premium or
  // Mix, so that asks too.
  const planQuestion = (next: HelpDeskPlan): string | null => {
    if (!evaRepliesSavedOn) return null;
    const nowAllowed = evaRepliesPlanAllowed(company.helpDeskPlan);
    const nextAllowed = evaRepliesPlanAllowed(next);
    if (nowAllowed && !nextAllowed) return LITE_SWITCHES_EVA_OFF_TEXT;
    if (!nowAllowed && nextAllowed) return EVA_STARTS_TEXT;
    return null;
  };
  const [confirmPlan, setConfirmPlan] = useState<HelpDeskPlan | null>(null);
  useEffect(() => {
    if (locked) setConfirmPlan(null);
  }, [locked]);
  const savePlan = (next: HelpDeskPlan) =>
    void saver.save(
      'helpDeskPlan',
      next,
      `Help desk plan set to ${PLAN_OPTIONS.find((p) => p.value === next)?.label}`,
    );
  const pickPlan = (next: HelpDeskPlan) => {
    if (locked || next === savedPlan) return;
    if (planQuestion(next) !== null) {
      setConfirmPlan(next);
      return;
    }
    savePlan(next);
  };

  // ---- Ticket emails (SF-5126) ---------------------------------------------
  const savedMode: HelpDeskEmailMode =
    company.help_desk_email_mode === 'forwarding' ? 'forwarding' : 'off';
  const pendingMode = pendingOf('help_desk_email_mode') as HelpDeskEmailMode | undefined;
  const pickMode = (next: HelpDeskEmailMode) => {
    if (locked || next === savedMode) return;
    void saver.save(
      'help_desk_email_mode',
      next,
      next === 'forwarding'
        ? 'Ticket emails now come from forwarding'
        : 'Ticket emails switched off',
    );
  };
  const forwardingAddress = company.help_desk_forwarding_address || null;
  const newAddress = useNewForwardingAddress();
  const makingAddress = saver.pending?.field === 'help_desk_forwarding_address';
  const [confirmNewAddress, setConfirmNewAddress] = useState(false);
  useEffect(() => {
    if (locked) setConfirmNewAddress(false);
  }, [locked]);
  const makeNewAddress = () => {
    const options: SaveFieldOptions = { send: newAddress };
    void saver.save('help_desk_forwarding_address', forwardingAddress, NEW_ADDRESS_MADE, options);
  };

  // ---- Support inbox --------------------------------------------------------
  const savedInbox = company.assets_support_email ?? '';
  const [inbox, setInbox] = useState(savedInbox);
  useEffect(() => {
    setInbox(savedInbox);
  }, [savedInbox]);
  const nextInbox = inbox.trim();

  // ---- Returning-customer window -------------------------------------------
  const savedDays = company.eva_helpdesk_repeat_window_days ?? REPEAT_WINDOW_DEFAULT;
  const [days, setDays] = useState<number | null>(savedDays);
  useEffect(() => {
    setDays(savedDays);
  }, [savedDays]);
  const daysValid =
    typeof days === 'number' &&
    Number.isInteger(days) &&
    days >= REPEAT_WINDOW_MIN &&
    days <= REPEAT_WINDOW_MAX;

  return (
    <Space direction="vertical" size="large" data-testid="help-desk-tab" style={{ width: '100%' }}>
      {scopeCheckFailed && !permissionsLoading ? (
        <Alert
          type="error"
          showIcon
          data-testid="help-desk-scope-error"
          message={SCOPE_CHECK_FAILED_NOTE}
          action={
            onRetryScope ? (
              <Button size="small" onClick={onRetryScope}>
                Retry
              </Button>
            ) : undefined
          }
        />
      ) : null}
      <Row
        testId="help-desk-plan"
        title="Help desk plan"
        note={reason ?? PLAN_OPTIONS.find((p) => p.value === (pendingPlan ?? savedPlan))?.note}
      >
        <Popconfirm
          title={`Move this company to ${PLAN_OPTIONS.find((p) => p.value === confirmPlan)?.label ?? 'this plan'}?`}
          description={confirmPlan ? planQuestion(confirmPlan) : null}
          okText="Change plan"
          open={confirmPlan !== null && !locked}
          onOpenChange={(open) => {
            if (!open) setConfirmPlan(null);
          }}
          onConfirm={() => {
            const next = confirmPlan;
            setConfirmPlan(null);
            if (next && !locked) savePlan(next);
          }}
        >
          <Radio.Group
            aria-label="Help desk plan"
            // Its own name, so a click on another radio group can never uncheck this one.
            name="help-desk-plan"
            value={pendingPlan ?? savedPlan}
            disabled={locked}
            onChange={(e) => pickPlan(e.target.value as HelpDeskPlan)}
          >
            {PLAN_OPTIONS.map((p) => (
              <Radio key={p.value} value={p.value}>
                {p.label}
              </Radio>
            ))}
          </Radio.Group>
        </Popconfirm>
      </Row>

      <Row
        testId="help-desk-email-mode"
        title="Ticket emails"
        note={
          reason ?? EMAIL_MODE_OPTIONS.find((o) => o.value === (pendingMode ?? savedMode))?.note
        }
      >
        <Radio.Group
          aria-label="Ticket emails"
          name="help-desk-email-mode"
          value={pendingMode ?? savedMode}
          disabled={locked}
          onChange={(e) => pickMode(e.target.value as HelpDeskEmailMode)}
        >
          {EMAIL_MODE_OPTIONS.map((o) => (
            <Radio key={o.value} value={o.value}>
              {o.label}
            </Radio>
          ))}
        </Radio.Group>
      </Row>

      {savedMode === 'forwarding' ? (
        <Row
          testId="help-desk-forwarding-address"
          title="Forwarding address"
          note={reason ?? 'Give this address to the company to forward its support mailbox to.'}
        >
          <Space wrap>
            {forwardingAddress ? (
              <Text code copyable>
                {forwardingAddress}
              </Text>
            ) : (
              <Text type="warning">Address not made yet</Text>
            )}
            <Popconfirm
              title="Make a new forwarding address?"
              description="The current address stops working at once. The company has to change its forwarding to the new one."
              okText="Make new address"
              open={confirmNewAddress && !locked}
              onOpenChange={(open) => setConfirmNewAddress(open && !locked)}
              onConfirm={() => {
                setConfirmNewAddress(false);
                if (!locked) makeNewAddress();
              }}
            >
              <Button disabled={locked} loading={makingAddress}>
                Make a new address
              </Button>
            </Popconfirm>
          </Space>
        </Row>
      ) : null}

      <Row
        testId="help-desk-support-inbox"
        title="Support inbox address"
        note={
          reason ??
          "The company's own support address, which it forwards to the forwarding address. Shown here for reference; emails are only accepted at the forwarding address."
        }
      >
        <Space.Compact style={{ display: 'flex', maxWidth: 480 }}>
          <Input
            aria-label="Support inbox address"
            type="email"
            value={inbox}
            placeholder="support@example.com"
            disabled={locked}
            onChange={(e) => setInbox(e.target.value)}
          />
          <Button
            disabled={locked || nextInbox === savedInbox}
            loading={pendingOf('assets_support_email') !== undefined}
            onClick={() =>
              void saver.save(
                'assets_support_email',
                nextInbox,
                nextInbox ? 'Support inbox saved' : 'Support inbox removed',
              )
            }
          >
            Save
          </Button>
        </Space.Compact>
      </Row>

      <Row
        testId="help-desk-ratings"
        title="Ratings create tickets"
        note={
          reason ??
          'A rating that opens a complaint also opens a help desk ticket, at locations that have this switched on too (set on the location in the web app). Saved immediately.'
        }
      >
        <FlagSwitch
          testId="help-desk-ratings-toggle"
          ariaLabel="Ratings create tickets"
          checked={company.allowRatingHelpdesk === true}
          pendingValue={pendingBool('allowRatingHelpdesk')}
          loading={permissionsLoading}
          disabled={locked}
          disabledReason={reason}
          confirmOff={{
            title: 'Stop ratings creating tickets?',
            description:
              'New ratings no longer open help desk tickets. Tickets already open are kept.',
            okText: 'Switch off',
          }}
          onChange={(next) =>
            void saver.save(
              'allowRatingHelpdesk',
              next,
              next ? 'Ratings now create tickets' : 'Ratings no longer create tickets',
            )
          }
        />
      </Row>

      <EvaHelpdeskRepliesFlag
        company={company}
        canEditCompany={canEditCompany}
        companyOnList={companyOnList}
        scopeCheckFailed={scopeCheckFailed}
        permissionsLoading={permissionsLoading}
        saver={saver}
      />

      <Row
        testId="help-desk-repeat-window"
        title="Returning customer (days)"
        note={reason ?? repeatWindowNote(savedDays)}
      >
        <Space.Compact>
          <InputNumber
            aria-label="Returning customer (days)"
            min={REPEAT_WINDOW_MIN}
            max={REPEAT_WINDOW_MAX}
            precision={0}
            value={days}
            disabled={locked}
            onChange={(v) => setDays(typeof v === 'number' ? v : null)}
          />
          <Button
            disabled={locked || !daysValid || days === savedDays}
            loading={pendingOf('eva_helpdesk_repeat_window_days') !== undefined}
            onClick={() => {
              if (!daysValid) return;
              void saver.save(
                'eva_helpdesk_repeat_window_days',
                days,
                days === 0
                  ? 'Returning customer check switched off'
                  : `Returning customer window set to ${daysText(days)}`,
              );
            }}
          >
            Save
          </Button>
        </Space.Compact>
      </Row>
    </Space>
  );
};
